import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import { Shield, Database, Eye, Lock, Globe, UserCheck, Mail, Clock } from 'lucide-react';

const PrivacyPage = () => {
  const { currentLanguage } = useLanguage();

  const translations = {
    en: {
      badge: "Legal",
      title: 'Privacy Policy',
      lastUpdated: 'Last Updated',
      intro: 'At PenLab Peptide, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or make a purchase.',
      sections: [
        {
          icon: Database,
          title: '1. Information We Collect',
          content: `We collect information that you provide directly to us, including:

Personal Information:
• Name and contact details (email, phone number, address)
• Professional credentials and institutional affiliation
• License/registration numbers for verification purposes
• Payment and billing information

Automatically Collected Information:
• IP address and browser type
• Device information and operating system
• Pages visited and time spent on our website
• Cookies and similar tracking technologies

We collect this information to verify your eligibility, process orders, and improve our services.`
        },
        {
          icon: Eye,
          title: '2. How We Use Your Information',
          content: `We use the information we collect for the following purposes:

• Account verification and eligibility confirmation
• Processing and fulfilling your orders
• Communicating with you about your orders and account
• Sending important updates about our products and services
• Improving our website and customer experience
• Complying with legal obligations and regulatory requirements
• Preventing fraud and ensuring security
• Analyzing usage patterns to enhance our services

We will never sell your personal information to third parties for marketing purposes.`
        },
        {
          icon: Lock,
          title: '3. Data Security',
          content: `We implement appropriate technical and organizational measures to protect your personal information, including:

• SSL/TLS encryption for all data transmissions
• Secure servers with restricted access
• Regular security audits and vulnerability assessments
• Employee training on data protection practices
• Secure payment processing through trusted providers

While we strive to protect your information, no method of transmission over the Internet is 100% secure. We cannot guarantee absolute security but are committed to maintaining the highest standards of data protection.`
        },
        {
          icon: Globe,
          title: '4. Information Sharing',
          content: `We may share your information with:

Service Providers:
• Shipping carriers (DHL, etc.) for order delivery
• Payment processors for transaction processing
• IT service providers for website maintenance

Legal Requirements:
• When required by law or legal process
• To protect our rights and property
• To prevent fraud or illegal activities
• To comply with regulatory obligations

Business Transfers:
• In connection with any merger, acquisition, or sale of assets

We require all third parties to respect your privacy and handle your data in accordance with applicable laws.`
        },
        {
          icon: UserCheck,
          title: '5. Your Rights',
          content: `Under applicable data protection laws (including GDPR), you have the right to:

• Access: Request a copy of your personal data
• Rectification: Correct inaccurate or incomplete data
• Erasure: Request deletion of your data ("right to be forgotten")
• Restriction: Limit how we process your data
• Portability: Receive your data in a portable format
• Objection: Object to certain processing activities
• Withdraw Consent: Withdraw consent at any time

To exercise these rights, please contact us at info@penlabpeptide.com. We will respond to your request within 30 days.`
        },
        {
          icon: Clock,
          title: '6. Data Retention',
          content: `We retain your personal information for as long as necessary to:

• Fulfill the purposes for which it was collected
• Comply with legal and regulatory requirements
• Resolve disputes and enforce our agreements
• Maintain business records as required by law

Typically, we retain:
• Account information: Duration of your account plus 7 years
• Order records: 10 years for tax and legal compliance
• Marketing preferences: Until you opt out
• Website analytics: 26 months

After the retention period, your data will be securely deleted or anonymized.`
        },
        {
          icon: Shield,
          title: '7. Cookies & Tracking',
          content: `Our website uses cookies and similar technologies to:

Essential Cookies:
• Enable core website functionality
• Maintain your session and shopping cart
• Remember your language preferences

Analytics Cookies:
• Understand how visitors use our website
• Improve user experience and performance
• Generate anonymous usage statistics

You can control cookies through your browser settings. Note that disabling certain cookies may affect website functionality.

We do not use cookies for third-party advertising or tracking across other websites.`
        },
        {
          icon: Mail,
          title: '8. Contact & Updates',
          content: `If you have questions about this Privacy Policy or our data practices, please contact us:

Email: info@penlabpeptide.com
Address: España, Europa

Policy Updates:
We may update this Privacy Policy periodically to reflect changes in our practices or legal requirements. We will notify you of significant changes by:
• Posting a notice on our website
• Sending an email to registered users
• Updating the "Last Updated" date

We encourage you to review this policy regularly to stay informed about how we protect your information.`
        }
      ],
      contact: 'Contact Our Privacy Team',
      contactText: 'For any privacy-related questions or to exercise your data rights, please contact us at:',
      email: 'info@penlabpeptide.com'
    },
    es: {
      badge: "Legal",
      title: 'Política de Privacidad',
      lastUpdated: 'Última Actualización',
      intro: 'En PenLab Peptide, estamos comprometidos con la protección de su privacidad y la seguridad de su información personal. Esta Política de Privacidad explica cómo recopilamos, utilizamos, divulgamos y protegemos su información cuando visita nuestro sitio web o realiza una compra.',
      sections: [
        {
          icon: Database,
          title: '1. Información que Recopilamos',
          content: `Recopilamos información que usted nos proporciona directamente, incluyendo:

Información Personal:
• Nombre y datos de contacto (correo electrónico, número de teléfono, dirección)
• Credenciales profesionales y afiliación institucional
• Números de licencia/registro para fines de verificación
• Información de pago y facturación

Información Recopilada Automáticamente:
• Dirección IP y tipo de navegador
• Información del dispositivo y sistema operativo
• Páginas visitadas y tiempo en nuestro sitio web
• Cookies y tecnologías de seguimiento similares

Recopilamos esta información para verificar su elegibilidad, procesar pedidos y mejorar nuestros servicios.`
        },
        {
          icon: Eye,
          title: '2. Cómo Utilizamos su Información',
          content: `Utilizamos la información que recopilamos para los siguientes propósitos:

• Verificación de cuenta y confirmación de elegibilidad
• Procesamiento y cumplimiento de sus pedidos
• Comunicarnos con usted sobre sus pedidos y cuenta
• Enviar actualizaciones importantes sobre nuestros productos y servicios
• Mejorar nuestro sitio web y experiencia del cliente
• Cumplir con obligaciones legales y requisitos regulatorios
• Prevenir fraudes y garantizar la seguridad
• Analizar patrones de uso para mejorar nuestros servicios

Nunca venderemos su información personal a terceros con fines de marketing.`
        },
        {
          icon: Lock,
          title: '3. Seguridad de Datos',
          content: `Implementamos medidas técnicas y organizativas apropiadas para proteger su información personal, incluyendo:

• Encriptación SSL/TLS para todas las transmisiones de datos
• Servidores seguros con acceso restringido
• Auditorías de seguridad regulares y evaluaciones de vulnerabilidades
• Capacitación de empleados en prácticas de protección de datos
• Procesamiento seguro de pagos a través de proveedores confiables

Aunque nos esforzamos por proteger su información, ningún método de transmisión por Internet es 100% seguro. No podemos garantizar seguridad absoluta, pero estamos comprometidos a mantener los más altos estándares de protección de datos.`
        },
        {
          icon: Globe,
          title: '4. Compartir Información',
          content: `Podemos compartir su información con:

Proveedores de Servicios:
• Transportistas (DHL, etc.) para entrega de pedidos
• Procesadores de pago para procesamiento de transacciones
• Proveedores de servicios de TI para mantenimiento del sitio web

Requisitos Legales:
• Cuando lo requiera la ley o proceso legal
• Para proteger nuestros derechos y propiedad
• Para prevenir fraude o actividades ilegales
• Para cumplir con obligaciones regulatorias

Transferencias Comerciales:
• En conexión con cualquier fusión, adquisición o venta de activos

Requerimos que todos los terceros respeten su privacidad y manejen sus datos de acuerdo con las leyes aplicables.`
        },
        {
          icon: UserCheck,
          title: '5. Sus Derechos',
          content: `Bajo las leyes de protección de datos aplicables (incluyendo GDPR), usted tiene derecho a:

• Acceso: Solicitar una copia de sus datos personales
• Rectificación: Corregir datos inexactos o incompletos
• Supresión: Solicitar eliminación de sus datos ("derecho al olvido")
• Restricción: Limitar cómo procesamos sus datos
• Portabilidad: Recibir sus datos en formato portable
• Oposición: Oponerse a ciertas actividades de procesamiento
• Retirar Consentimiento: Retirar el consentimiento en cualquier momento

Para ejercer estos derechos, por favor contáctenos en info@penlabpeptide.com. Responderemos a su solicitud dentro de 30 días.`
        },
        {
          icon: Clock,
          title: '6. Retención de Datos',
          content: `Conservamos su información personal durante el tiempo necesario para:

• Cumplir los propósitos para los que fue recopilada
• Cumplir con requisitos legales y regulatorios
• Resolver disputas y hacer cumplir nuestros acuerdos
• Mantener registros comerciales según lo requiera la ley

Típicamente, conservamos:
• Información de cuenta: Duración de su cuenta más 7 años
• Registros de pedidos: 10 años para cumplimiento fiscal y legal
• Preferencias de marketing: Hasta que usted cancele
• Análisis del sitio web: 26 meses

Después del período de retención, sus datos serán eliminados de forma segura o anonimizados.`
        },
        {
          icon: Shield,
          title: '7. Cookies y Seguimiento',
          content: `Nuestro sitio web utiliza cookies y tecnologías similares para:

Cookies Esenciales:
• Habilitar la funcionalidad principal del sitio web
• Mantener su sesión y carrito de compras
• Recordar sus preferencias de idioma

Cookies de Análisis:
• Comprender cómo los visitantes usan nuestro sitio web
• Mejorar la experiencia del usuario y el rendimiento
• Generar estadísticas de uso anónimas

Puede controlar las cookies a través de la configuración de su navegador. Tenga en cuenta que deshabilitar ciertas cookies puede afectar la funcionalidad del sitio web.

No utilizamos cookies para publicidad de terceros ni seguimiento en otros sitios web.`
        },
        {
          icon: Mail,
          title: '8. Contacto y Actualizaciones',
          content: `Si tiene preguntas sobre esta Política de Privacidad o nuestras prácticas de datos, por favor contáctenos:

Email: info@penlabpeptide.com
Dirección: España, Europa

Actualizaciones de Política:
Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas o requisitos legales. Le notificaremos de cambios significativos mediante:
• Publicar un aviso en nuestro sitio web
• Enviar un correo electrónico a usuarios registrados
• Actualizar la fecha de "Última Actualización"

Le animamos a revisar esta política regularmente para mantenerse informado sobre cómo protegemos su información.`
        }
      ],
      contact: 'Contacte a Nuestro Equipo de Privacidad',
      contactText: 'Para cualquier pregunta relacionada con la privacidad o para ejercer sus derechos de datos, por favor contáctenos en:',
      email: 'info@penlabpeptide.com'
    },
    ru: {
      badge: "Правовая информация",
      title: 'Политика Конфиденциальности',
      lastUpdated: 'Последнее Обновление',
      intro: 'В PenLab Peptide мы стремимся защитить вашу конфиденциальность и обеспечить безопасность вашей личной информации. Настоящая Политика конфиденциальности объясняет, как мы собираем, используем, раскрываем и защищаем вашу информацию при посещении нашего веб-сайта или совершении покупки.',
      sections: [
        {
          icon: Database,
          title: '1. Информация, Которую Мы Собираем',
          content: `Мы собираем информацию, которую вы предоставляете нам напрямую, включая:

Личная Информация:
• Имя и контактные данные (электронная почта, номер телефона, адрес)
• Профессиональные полномочия и институциональная принадлежность
• Номера лицензий/регистрации для целей верификации
• Информация о платежах и выставлении счетов

Автоматически Собираемая Информация:
• IP-адрес и тип браузера
• Информация об устройстве и операционной системе
• Посещенные страницы и время на нашем сайте
• Файлы cookie и аналогичные технологии отслеживания

Мы собираем эту информацию для проверки вашего права на участие, обработки заказов и улучшения наших услуг.`
        },
        {
          icon: Eye,
          title: '2. Как Мы Используем Вашу Информацию',
          content: `Мы используем собранную информацию для следующих целей:

• Верификация аккаунта и подтверждение права на участие
• Обработка и выполнение ваших заказов
• Связь с вами по поводу ваших заказов и аккаунта
• Отправка важных обновлений о наших продуктах и услугах
• Улучшение нашего веб-сайта и клиентского опыта
• Соблюдение юридических обязательств и нормативных требований
• Предотвращение мошенничества и обеспечение безопасности
• Анализ моделей использования для улучшения наших услуг

Мы никогда не будем продавать вашу личную информацию третьим лицам в маркетинговых целях.`
        },
        {
          icon: Lock,
          title: '3. Безопасность Данных',
          content: `Мы внедряем соответствующие технические и организационные меры для защиты вашей личной информации, включая:

• Шифрование SSL/TLS для всех передач данных
• Защищенные серверы с ограниченным доступом
• Регулярные аудиты безопасности и оценки уязвимостей
• Обучение сотрудников практикам защиты данных
• Безопасная обработка платежей через надежных провайдеров

Хотя мы стремимся защитить вашу информацию, ни один метод передачи через Интернет не является на 100% безопасным. Мы не можем гарантировать абсолютную безопасность, но привержены поддержанию высочайших стандартов защиты данных.`
        },
        {
          icon: Globe,
          title: '4. Обмен Информацией',
          content: `Мы можем делиться вашей информацией с:

Поставщики Услуг:
• Транспортные компании (DHL и др.) для доставки заказов
• Платежные процессоры для обработки транзакций
• ИТ-поставщики для обслуживания веб-сайта

Юридические Требования:
• Когда это требуется по закону или судебному процессу
• Для защиты наших прав и собственности
• Для предотвращения мошенничества или незаконной деятельности
• Для соблюдения нормативных обязательств

Бизнес-Переводы:
• В связи с любым слиянием, приобретением или продажей активов

Мы требуем от всех третьих сторон уважать вашу конфиденциальность и обрабатывать ваши данные в соответствии с применимыми законами.`
        },
        {
          icon: UserCheck,
          title: '5. Ваши Права',
          content: `В соответствии с применимыми законами о защите данных (включая GDPR), вы имеете право:

• Доступ: Запросить копию ваших личных данных
• Исправление: Исправить неточные или неполные данные
• Удаление: Запросить удаление ваших данных ("право на забвение")
• Ограничение: Ограничить обработку ваших данных
• Переносимость: Получить ваши данные в переносимом формате
• Возражение: Возражать против определенных операций обработки
• Отзыв Согласия: Отозвать согласие в любое время

Для осуществления этих прав, пожалуйста, свяжитесь с нами по адресу info@penlabpeptide.com. Мы ответим на ваш запрос в течение 30 дней.`
        },
        {
          icon: Clock,
          title: '6. Хранение Данных',
          content: `Мы храним вашу личную информацию столько, сколько необходимо для:

• Выполнения целей, для которых она была собрана
• Соблюдения юридических и нормативных требований
• Разрешения споров и исполнения наших соглашений
• Ведения деловых записей согласно требованиям закона

Обычно мы храним:
• Информацию об аккаунте: Срок действия вашего аккаунта плюс 7 лет
• Записи о заказах: 10 лет для налогового и правового соответствия
• Маркетинговые предпочтения: До вашего отказа
• Аналитика веб-сайта: 26 месяцев

После окончания срока хранения ваши данные будут безопасно удалены или анонимизированы.`
        },
        {
          icon: Shield,
          title: '7. Файлы Cookie и Отслеживание',
          content: `Наш веб-сайт использует файлы cookie и аналогичные технологии для:

Необходимые Cookie:
• Включение основной функциональности веб-сайта
• Поддержание вашей сессии и корзины покупок
• Запоминание ваших языковых предпочтений

Аналитические Cookie:
• Понимание того, как посетители используют наш веб-сайт
• Улучшение пользовательского опыта и производительности
• Генерация анонимной статистики использования

Вы можете контролировать cookie через настройки вашего браузера. Обратите внимание, что отключение определенных cookie может повлиять на функциональность веб-сайта.

Мы не используем cookie для сторонней рекламы или отслеживания на других веб-сайтах.`
        },
        {
          icon: Mail,
          title: '8. Контакт и Обновления',
          content: `Если у вас есть вопросы об этой Политике конфиденциальности или наших практиках работы с данными, пожалуйста, свяжитесь с нами:

Email: info@penlabpeptide.com
Адрес: Испания, Европа

Обновления Политики:
Мы можем периодически обновлять эту Политику конфиденциальности, чтобы отразить изменения в наших практиках или юридических требованиях. Мы уведомим вас о значительных изменениях путем:
• Размещения уведомления на нашем веб-сайте
• Отправки электронного письма зарегистрированным пользователям
• Обновления даты "Последнее обновление"

Мы рекомендуем вам регулярно просматривать эту политику, чтобы быть в курсе того, как мы защищаем вашу информацию.`
        }
      ],
      contact: 'Свяжитесь с Нашей Командой по Конфиденциальности',
      contactText: 'По любым вопросам, связанным с конфиденциальностью, или для осуществления ваших прав на данные, пожалуйста, свяжитесь с нами:',
      email: 'info@penlabpeptide.com'
    },
    de: {
      badge: "Rechtliches",
      title: 'Datenschutzerklärung',
      lastUpdated: 'Zuletzt aktualisiert',
      intro: 'Bei PenLab Peptide sind wir dem Schutz Ihrer Privatsphäre und der Sicherung Ihrer personenbezogenen Daten verpflichtet. Diese Datenschutzerklärung erläutert, wie wir Ihre Daten erfassen, verwenden, offenlegen und schützen, wenn Sie unsere Website besuchen oder einen Kauf tätigen.',
      sections: [
        {
          icon: Database,
          title: '1. Welche Informationen wir erfassen',
          content: `Wir erfassen Informationen, die Sie uns direkt zur Verfügung stellen, einschließlich:

Personenbezogene Daten:
• Name und Kontaktdaten (E-Mail, Telefonnummer, Adresse)
• Berufliche Qualifikationen und institutionelle Zugehörigkeit
• Lizenz-/Registrierungsnummern zu Verifizierungszwecken
• Zahlungs- und Abrechnungsinformationen

Automatisch erfasste Informationen:
• IP-Adresse und Browsertyp
• Geräteinformationen und Betriebssystem
• Besuchte Seiten und Verweildauer auf unserer Website
• Cookies und ähnliche Tracking-Technologien

Wir erfassen diese Informationen, um Ihre Berechtigung zu überprüfen, Bestellungen zu bearbeiten und unsere Dienstleistungen zu verbessern.`
        },
        {
          icon: Eye,
          title: '2. Wie wir Ihre Informationen verwenden',
          content: `Wir verwenden die von uns erfassten Informationen für folgende Zwecke:

• Kontoverifizierung und Bestätigung der Berechtigung
• Bearbeitung und Ausführung Ihrer Bestellungen
• Kommunikation mit Ihnen über Ihre Bestellungen und Ihr Konto
• Versand wichtiger Aktualisierungen zu unseren Produkten und Dienstleistungen
• Verbesserung unserer Website und der Kundenerfahrung
• Erfüllung gesetzlicher Pflichten und regulatorischer Anforderungen
• Verhinderung von Betrug und Gewährleistung der Sicherheit
• Analyse von Nutzungsmustern zur Optimierung unserer Dienstleistungen

Wir werden Ihre personenbezogenen Daten niemals zu Marketingzwecken an Dritte verkaufen.`
        },
        {
          icon: Lock,
          title: '3. Datensicherheit',
          content: `Wir implementieren angemessene technische und organisatorische Maßnahmen zum Schutz Ihrer personenbezogenen Daten, darunter:

• SSL/TLS-Verschlüsselung für alle Datenübertragungen
• Sichere Server mit beschränktem Zugriff
• Regelmäßige Sicherheitsaudits und Schwachstellenbewertungen
• Schulung von Mitarbeitenden zu Datenschutzpraktiken
• Sichere Zahlungsabwicklung über vertrauenswürdige Anbieter

Obwohl wir uns bemühen, Ihre Daten zu schützen, ist keine Übertragungsmethode über das Internet zu 100 % sicher. Wir können keine absolute Sicherheit garantieren, verpflichten uns jedoch zur Einhaltung höchster Datenschutzstandards.`
        },
        {
          icon: Globe,
          title: '4. Weitergabe von Informationen',
          content: `Wir können Ihre Daten weitergeben an:

Dienstleister:
• Versanddienstleister (DHL usw.) für die Zustellung von Bestellungen
• Zahlungsdienstleister zur Abwicklung von Transaktionen
• IT-Dienstleister für die Wartung der Website

Gesetzliche Anforderungen:
• Wenn dies durch Gesetz oder Rechtsverfahren vorgeschrieben ist
• Zum Schutz unserer Rechte und unseres Eigentums
• Zur Verhinderung von Betrug oder rechtswidrigen Aktivitäten
• Zur Erfüllung regulatorischer Verpflichtungen

Unternehmensübertragungen:
• Im Zusammenhang mit Fusionen, Übernahmen oder Veräußerung von Vermögenswerten

Wir verpflichten alle Drittanbieter dazu, Ihre Privatsphäre zu respektieren und Ihre Daten in Übereinstimmung mit den geltenden Gesetzen zu behandeln.`
        },
        {
          icon: UserCheck,
          title: '5. Ihre Rechte',
          content: `Nach den geltenden Datenschutzgesetzen (einschließlich DSGVO) haben Sie das Recht auf:

• Auskunft: Anforderung einer Kopie Ihrer personenbezogenen Daten
• Berichtigung: Korrektur unrichtiger oder unvollständiger Daten
• Löschung: Anforderung der Löschung Ihrer Daten („Recht auf Vergessenwerden“)
• Einschränkung: Begrenzung der Art und Weise, wie wir Ihre Daten verarbeiten
• Datenübertragbarkeit: Erhalt Ihrer Daten in einem portablen Format
• Widerspruch: Widerspruch gegen bestimmte Verarbeitungstätigkeiten
• Widerruf der Einwilligung: Jederzeitiger Widerruf einer erteilten Einwilligung

Um diese Rechte auszuüben, kontaktieren Sie uns bitte unter info@penlabpeptide.com. Wir werden auf Ihre Anfrage innerhalb von 30 Tagen reagieren.`
        },
        {
          icon: Clock,
          title: '6. Aufbewahrung von Daten',
          content: `Wir speichern Ihre personenbezogenen Daten so lange, wie dies erforderlich ist, um:

• Die Zwecke zu erfüllen, für die sie erhoben wurden
• Gesetzliche und regulatorische Anforderungen zu erfüllen
• Streitigkeiten zu lösen und unsere Vereinbarungen durchzusetzen
• Geschäftliche Aufzeichnungen gemäß den gesetzlichen Vorgaben zu führen

Typischerweise speichern wir:
• Kontoinformationen: Dauer Ihres Kontos plus 7 Jahre
• Bestellunterlagen: 10 Jahre für Steuer- und Rechtskonformität
• Marketingpräferenzen: Bis Sie dem widersprechen (Opt-out)
• Website-Analysedaten: 26 Monate

Nach Ablauf der Aufbewahrungsfrist werden Ihre Daten sicher gelöscht oder anonymisiert.`
        },
        {
          icon: Shield,
          title: '7. Cookies & Tracking',
          content: `Unsere Website verwendet Cookies und ähnliche Technologien für folgende Zwecke:

Notwendige Cookies:
• Ermöglichen grundlegende Website-Funktionen
• Aufrechterhaltung Ihrer Sitzung und Ihres Warenkorbs
• Speichern Ihrer Spracheinstellungen

Analyse-Cookies:
• Verständnis darüber, wie Besucher unsere Website nutzen
• Verbesserung der Benutzererfahrung und Leistung
• Erstellung anonymer Nutzungsstatistiken

Sie können Cookies über Ihre Browsereinstellungen steuern. Bitte beachten Sie, dass die Deaktivierung bestimmter Cookies die Funktionalität der Website beeinträchtigen kann.

Wir verwenden keine Cookies für Werbung Dritter oder für Tracking über andere Websites hinweg.`
        },
        {
          icon: Mail,
          title: '8. Kontakt & Aktualisierungen',
          content: `Wenn Sie Fragen zu dieser Datenschutzerklärung oder unseren Datenverarbeitungspraktiken haben, kontaktieren Sie uns bitte:

E-Mail: info@penlabpeptide.com
Adresse: España, Europa

Aktualisierungen der Richtlinie:
Wir können diese Datenschutzerklärung regelmäßig aktualisieren, um Änderungen unserer Praktiken oder rechtlicher Anforderungen zu berücksichtigen. Wir werden Sie über wesentliche Änderungen informieren, indem wir:
• Einen Hinweis auf unserer Website veröffentlichen
• Eine E-Mail an registrierte Nutzer senden
• Das Datum „Zuletzt aktualisiert“ anpassen

Wir empfehlen Ihnen, diese Richtlinie regelmäßig zu überprüfen, um darüber informiert zu bleiben, wie wir Ihre Daten schützen.`
        }
      ],
      contact: 'Kontaktieren Sie unser Datenschutzteam',
      contactText: 'Für datenschutzbezogene Fragen oder zur Ausübung Ihrer Datenschutzrechte kontaktieren Sie uns bitte unter:',
      email: 'info@penlabpeptide.com'
    },
    fr: {
      badge: "Légal",
      title: 'Politique de confidentialité',
      lastUpdated: 'Dernière mise à jour',
      intro: 'Chez PenLab Peptide, nous nous engageons à protéger votre vie privée et à assurer la sécurité de vos informations personnelles. La présente Politique de confidentialité explique comment nous collectons, utilisons, divulguons et protégeons vos informations lorsque vous visitez notre site web ou effectuez un achat.',
      sections: [
        {
          icon: Database,
          title: '1. Informations que nous collectons',
          content: `Nous collectons les informations que vous nous fournissez directement, y compris :

Informations personnelles :
• Nom et coordonnées (e-mail, numéro de téléphone, adresse)
• Titres professionnels et affiliation institutionnelle
• Numéros de licence/enregistrement à des fins de vérification
• Informations de paiement et de facturation

Informations collectées automatiquement :
• Adresse IP et type de navigateur
• Informations sur l’appareil et système d’exploitation
• Pages visitées et temps passé sur notre site web
• Cookies et technologies de suivi similaires

Nous collectons ces informations pour vérifier votre éligibilité, traiter les commandes et améliorer nos services.`
        },
        {
          icon: Eye,
          title: '2. Comment nous utilisons vos informations',
          content: `Nous utilisons les informations que nous collectons aux fins suivantes :

• Vérification du compte et confirmation d’éligibilité
• Traitement et exécution de vos commandes
• Communication avec vous au sujet de vos commandes et de votre compte
• Envoi de mises à jour importantes concernant nos produits et services
• Amélioration de notre site web et de l’expérience client
• Respect des obligations légales et des exigences réglementaires
• Prévention de la fraude et garantie de la sécurité
• Analyse des modes d’utilisation afin d’améliorer nos services

Nous ne vendrons jamais vos informations personnelles à des tiers à des fins de marketing.`
        },
        {
          icon: Lock,
          title: '3. Sécurité des données',
          content: `Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos informations personnelles, notamment :

• Chiffrement SSL/TLS pour toutes les transmissions de données
• Serveurs sécurisés avec accès restreint
• Audits de sécurité réguliers et évaluations de vulnérabilité
• Formation des employés aux pratiques de protection des données
• Traitement sécurisé des paiements via des prestataires de confiance

Bien que nous nous efforcions de protéger vos informations, aucune méthode de transmission sur Internet n’est sûre à 100 %. Nous ne pouvons pas garantir une sécurité absolue, mais nous nous engageons à maintenir les normes les plus élevées en matière de protection des données.`
        },
        {
          icon: Globe,
          title: '4. Partage des informations',
          content: `Nous pouvons partager vos informations avec :

Prestataires de services :
• Transporteurs (DHL, etc.) pour la livraison des commandes
• Processeurs de paiement pour le traitement des transactions
• Prestataires de services informatiques pour la maintenance du site web

Exigences légales :
• Lorsque la loi ou une procédure judiciaire l’exige
• Pour protéger nos droits et nos biens
• Pour prévenir la fraude ou les activités illégales
• Pour nous conformer aux obligations réglementaires

Transferts d’entreprise :
• Dans le cadre de toute fusion, acquisition ou cession d’actifs

Nous exigeons de tous les tiers qu’ils respectent votre vie privée et traitent vos données conformément aux lois applicables.`
        },
        {
          icon: UserCheck,
          title: '5. Vos droits',
          content: `En vertu des lois applicables en matière de protection des données (y compris le RGPD), vous avez le droit de :

• Accès : Demander une copie de vos données personnelles
• Rectification : Corriger les données inexactes ou incomplètes
• Effacement : Demander la suppression de vos données ("droit à l’oubli")
• Limitation : Limiter la manière dont nous traitons vos données
• Portabilité : Recevoir vos données dans un format portable
• Opposition : Vous opposer à certaines activités de traitement
• Retrait du consentement : Retirer votre consentement à tout moment

Pour exercer ces droits, veuillez nous contacter à info@penlabpeptide.com. Nous répondrons à votre demande dans un délai de 30 jours.`
        },
        {
          icon: Clock,
          title: '6. Conservation des données',
          content: `Nous conservons vos informations personnelles aussi longtemps que nécessaire pour :

• Atteindre les objectifs pour lesquels elles ont été collectées
• Respecter les exigences légales et réglementaires
• Résoudre les litiges et faire appliquer nos accords
• Tenir des registres commerciaux conformément à la loi

En règle générale, nous conservons :
• Informations de compte : Durée de vie de votre compte plus 7 ans
• Dossiers de commande : 10 ans pour la conformité fiscale et légale
• Préférences marketing : Jusqu’à ce que vous vous désinscriviez
• Données d’analytique du site web : 26 mois

À l’issue de la période de conservation, vos données seront supprimées de manière sécurisée ou rendues anonymes.`
        },
        {
          icon: Shield,
          title: '7. Cookies et suivi',
          content: `Notre site web utilise des cookies et des technologies similaires pour :

Cookies essentiels :
• Activer les fonctionnalités de base du site web
• Maintenir votre session et votre panier
• Mémoriser vos préférences linguistiques

Cookies d’analytique :
• Comprendre comment les visiteurs utilisent notre site web
• Améliorer l’expérience utilisateur et les performances
• Générer des statistiques d’utilisation anonymes

Vous pouvez contrôler les cookies via les paramètres de votre navigateur. Notez que la désactivation de certains cookies peut affecter les fonctionnalités du site.

Nous n’utilisons pas de cookies pour la publicité de tiers ni pour le suivi sur d’autres sites web.`
        },
        {
          icon: Mail,
          title: '8. Contact et mises à jour',
          content: `Si vous avez des questions concernant cette Politique de confidentialité ou nos pratiques en matière de données, veuillez nous contacter :

E-mail : info@penlabpeptide.com
Adresse : España, Europa

Mises à jour de la politique :
Nous pouvons mettre à jour périodiquement cette Politique de confidentialité afin de refléter les changements dans nos pratiques ou les exigences légales. Nous vous informerons des changements importants en :
• Affichant un avis sur notre site web
• Envoyant un e-mail aux utilisateurs enregistrés
• Mettant à jour la date de "Dernière mise à jour"

Nous vous encourageons à consulter régulièrement cette politique afin de rester informé(e) de la manière dont nous protégeons vos informations.`
        }
      ],
      contact: 'Contacter notre équipe de confidentialité',
      contactText: 'Pour toute question relative à la confidentialité ou pour exercer vos droits en matière de données, veuillez nous contacter à :',
      email: 'info@penlabpeptide.com'
    },
    it: {
      badge: "Legale",
      title: 'Informativa sulla Privacy',
      lastUpdated: 'Ultimo aggiornamento',
      intro: 'In PenLab Peptide, ci impegniamo a proteggere la tua privacy e a garantire la sicurezza delle tue informazioni personali. La presente Informativa sulla Privacy spiega come raccogliamo, utilizziamo, divulghiamo e tuteliamo le tue informazioni quando visiti il nostro sito web o effettui un acquisto.',
      sections: [
        {
          icon: Database,
          title: '1. Informazioni che raccogliamo',
          content: `Raccogliamo le informazioni che ci fornisci direttamente, incluse:

Informazioni personali:
• Nome e dati di contatto (email, numero di telefono, indirizzo)
• Credenziali professionali e affiliazione istituzionale
• Numeri di licenza/registrazione per finalità di verifica
• Informazioni di pagamento e fatturazione

Informazioni raccolte automaticamente:
• Indirizzo IP e tipo di browser
• Informazioni sul dispositivo e sistema operativo
• Pagine visitate e tempo trascorso sul nostro sito web
• Cookie e tecnologie di tracciamento simili

Raccogliamo queste informazioni per verificare la tua idoneità, elaborare gli ordini e migliorare i nostri servizi.`
        },
        {
          icon: Eye,
          title: '2. Come utilizziamo le tue informazioni',
          content: `Utilizziamo le informazioni che raccogliamo per le seguenti finalità:

• Verifica dell'account e conferma dell'idoneità
• Elaborazione ed evasione dei tuoi ordini
• Comunicazione con te riguardo ai tuoi ordini e al tuo account
• Invio di aggiornamenti importanti sui nostri prodotti e servizi
• Miglioramento del nostro sito web e dell'esperienza del cliente
• Adempimento di obblighi legali e requisiti normativi
• Prevenzione delle frodi e garanzia della sicurezza
• Analisi dei modelli di utilizzo per migliorare i nostri servizi

Non venderemo mai le tue informazioni personali a terzi per scopi di marketing.`
        },
        {
          icon: Lock,
          title: '3. Sicurezza dei dati',
          content: `Adottiamo misure tecniche e organizzative adeguate per proteggere le tue informazioni personali, tra cui:

• Crittografia SSL/TLS per tutte le trasmissioni di dati
• Server sicuri con accesso limitato
• Audit di sicurezza regolari e valutazioni delle vulnerabilità
• Formazione dei dipendenti sulle pratiche di protezione dei dati
• Elaborazione sicura dei pagamenti tramite provider affidabili

Sebbene ci impegniamo a proteggere le tue informazioni, nessun metodo di trasmissione su Internet è sicuro al 100%. Non possiamo garantire una sicurezza assoluta, ma ci impegniamo a mantenere i più alti standard di protezione dei dati.`
        },
        {
          icon: Globe,
          title: '4. Condivisione delle informazioni',
          content: `Potremmo condividere le tue informazioni con:

Fornitori di servizi:
• Corrieri di spedizione (DHL, ecc.) per la consegna degli ordini
• Processori di pagamento per l'elaborazione delle transazioni
• Fornitori di servizi IT per la manutenzione del sito web

Obblighi legali:
• Quando richiesto dalla legge o da un procedimento legale
• Per proteggere i nostri diritti e la nostra proprietà
• Per prevenire frodi o attività illegali
• Per rispettare gli obblighi normativi

Trasferimenti aziendali:
• In relazione a fusioni, acquisizioni o cessioni di beni

Richiediamo a tutte le terze parti di rispettare la tua privacy e di trattare i tuoi dati in conformità alle leggi applicabili.`
        },
        {
          icon: UserCheck,
          title: '5. I tuoi diritti',
          content: `Ai sensi delle leggi applicabili in materia di protezione dei dati (incluso il GDPR), hai il diritto di:

• Accesso: Richiedere una copia dei tuoi dati personali
• Rettifica: Correggere dati inesatti o incompleti
• Cancellazione: Richiedere la cancellazione dei tuoi dati ("diritto all'oblio")
• Limitazione: Limitare le modalità con cui trattiamo i tuoi dati
• Portabilità: Ricevere i tuoi dati in un formato portabile
• Opposizione: Opporti a determinate attività di trattamento
• Revoca del consenso: Revocare il consenso in qualsiasi momento

Per esercitare questi diritti, ti preghiamo di contattarci all'indirizzo info@penlabpeptide.com. Risponderemo alla tua richiesta entro 30 giorni.`
        },
        {
          icon: Clock,
          title: '6. Conservazione dei dati',
          content: `Conserviamo le tue informazioni personali per tutto il tempo necessario a:

• Raggiungere le finalità per cui sono state raccolte
• Soddisfare requisiti legali e normativi
• Risolvere controversie e far valere i nostri accordi
• Mantenere registri aziendali come richiesto dalla legge

In genere, conserviamo:
• Informazioni sull'account: Durata del tuo account più 7 anni
• Registri degli ordini: 10 anni per conformità fiscale e legale
• Preferenze di marketing: Fino a quando non annulli l'iscrizione
• Dati di analisi del sito web: 26 mesi

Al termine del periodo di conservazione, i tuoi dati saranno eliminati in modo sicuro o anonimizzati.`
        },
        {
          icon: Shield,
          title: '7. Cookie e tracciamento',
          content: `Il nostro sito web utilizza cookie e tecnologie simili per:

Cookie essenziali:
• Abilitare le funzionalità principali del sito web
• Mantenere la tua sessione e il carrello
• Ricordare le tue preferenze linguistiche

Cookie di analisi:
• Comprendere come i visitatori utilizzano il nostro sito web
• Migliorare l'esperienza utente e le prestazioni
• Generare statistiche di utilizzo anonime

Puoi controllare i cookie tramite le impostazioni del tuo browser. Tieni presente che la disabilitazione di alcuni cookie può influire sulla funzionalità del sito web.

Non utilizziamo cookie per pubblicità di terze parti o per il tracciamento su altri siti web.`
        },
        {
          icon: Mail,
          title: '8. Contatti e aggiornamenti',
          content: `Se hai domande su questa Informativa sulla Privacy o sulle nostre pratiche relative ai dati, ti preghiamo di contattarci:

Email: info@penlabpeptide.com
Indirizzo: España, Europa

Aggiornamenti dell'informativa:
Potremmo aggiornare periodicamente la presente Informativa sulla Privacy per riflettere modifiche alle nostre pratiche o ai requisiti legali. Ti informeremo delle modifiche significative tramite:
• Pubblicazione di un avviso sul nostro sito web
• Invio di un'email agli utenti registrati
• Aggiornamento della data di "Ultimo aggiornamento"

Ti invitiamo a consultare regolarmente questa informativa per rimanere informato su come proteggiamo le tue informazioni.`
        }
      ],
      contact: 'Contatta il nostro team per la privacy',
      contactText: 'Per qualsiasi domanda relativa alla privacy o per esercitare i tuoi diritti sui dati, ti preghiamo di contattarci a:',
      email: 'info@penlabpeptide.com'
    },
    pl: {
      badge: "Prawne",
      title: 'Polityka prywatności',
      lastUpdated: 'Ostatnia aktualizacja',
      intro: 'W PenLab Peptide dokładamy starań, aby chronić Twoją prywatność i zapewnić bezpieczeństwo Twoich danych osobowych. Niniejsza Polityka prywatności wyjaśnia, w jaki sposób zbieramy, wykorzystujemy, ujawniamy i zabezpieczamy Twoje dane podczas odwiedzania naszej strony internetowej lub dokonywania zakupu.',
      sections: [
        {
          icon: Database,
          title: '1. Informacje, które zbieramy',
          content: `Zbieramy informacje, które przekazujesz nam bezpośrednio, w tym:

Dane osobowe:
• Imię i nazwisko oraz dane kontaktowe (e‑mail, numer telefonu, adres)
• Kwalifikacje zawodowe i powiązania instytucjonalne
• Numery licencji/rejestracji do celów weryfikacji
• Informacje dotyczące płatności i rozliczeń

Informacje zbierane automatycznie:
• Adres IP i typ przeglądarki
• Informacje o urządzeniu i systemie operacyjnym
• Odwiedzane strony i czas spędzony na naszej stronie
• Pliki cookie i podobne technologie śledzące

Zbieramy te informacje, aby zweryfikować Twoją kwalifikowalność, przetwarzać zamówienia oraz ulepszać nasze usługi.`
        },
        {
          icon: Eye,
          title: '2. Jak wykorzystujemy Twoje dane',
          content: `Wykorzystujemy zebrane dane w następujących celach:

• Weryfikacja konta i potwierdzenie kwalifikowalności
• Przetwarzanie i realizacja Twoich zamówień
• Komunikowanie się z Tobą w sprawie zamówień i konta
• Wysyłanie ważnych aktualizacji dotyczących naszych produktów i usług
• Ulepszanie naszej strony internetowej i doświadczeń użytkowników
• Wypełnianie obowiązków prawnych i regulacyjnych
• Zapobieganie oszustwom i zapewnianie bezpieczeństwa
• Analiza wzorców użytkowania w celu doskonalenia naszych usług

Nigdy nie będziemy sprzedawać Twoich danych osobowych stronom trzecim w celach marketingowych.`
        },
        {
          icon: Lock,
          title: '3. Bezpieczeństwo danych',
          content: `Wdrażamy odpowiednie środki techniczne i organizacyjne w celu ochrony Twoich danych osobowych, w tym:

• Szyfrowanie SSL/TLS dla wszystkich transmisji danych
• Bezpieczne serwery z ograniczonym dostępem
• Regularne audyty bezpieczeństwa i oceny podatności
• Szkolenia pracowników w zakresie ochrony danych
• Bezpieczne przetwarzanie płatności przez zaufanych dostawców

Chociaż dokładamy starań, aby chronić Twoje dane, żadna metoda transmisji przez Internet nie jest w 100% bezpieczna. Nie możemy zagwarantować absolutnego bezpieczeństwa, jednak zobowiązujemy się do utrzymywania najwyższych standardów ochrony danych.`
        },
        {
          icon: Globe,
          title: '4. Udostępnianie informacji',
          content: `Możemy udostępniać Twoje dane:

Dostawcom usług:
• Przewoźnikom (DHL itp.) w celu dostarczenia zamówień
• Procesorom płatności w celu obsługi transakcji
• Dostawcom usług IT w zakresie utrzymania strony internetowej

Wymogi prawne:
• Gdy jest to wymagane przez prawo lub procedurę prawną
• Aby chronić nasze prawa i własność
• Aby zapobiegać oszustwom lub działaniom nielegalnym
• Aby spełnić obowiązki regulacyjne

Przeniesienia biznesowe:
• W związku z fuzją, przejęciem lub sprzedażą aktywów

Wymagamy od wszystkich stron trzecich poszanowania Twojej prywatności i przetwarzania Twoich danych zgodnie z obowiązującymi przepisami.`
        },
        {
          icon: UserCheck,
          title: '5. Twoje prawa',
          content: `Na mocy obowiązujących przepisów o ochronie danych (w tym RODO) masz prawo do:

• Dostępu: Zażądania kopii swoich danych osobowych
• Sprostowania: Poprawienia nieprawidłowych lub niekompletnych danych
• Usunięcia: Zażądania usunięcia swoich danych („prawo do bycia zapomnianym”)
• Ograniczenia: Ograniczenia sposobu, w jaki przetwarzamy Twoje dane
• Przenoszenia: Otrzymania swoich danych w formacie umożliwiającym przeniesienie
• Sprzeciwu: Wniesienia sprzeciwu wobec określonych czynności przetwarzania
• Wycofania zgody: Wycofania zgody w dowolnym momencie

Aby skorzystać z tych praw, skontaktuj się z nami pod adresem info@penlabpeptide.com. Odpowiemy na Twoje żądanie w ciągu 30 dni.`
        },
        {
          icon: Clock,
          title: '6. Okres przechowywania danych',
          content: `Przechowujemy Twoje dane osobowe przez okres niezbędny do:

• Realizacji celów, dla których zostały zebrane
• Spełnienia wymogów prawnych i regulacyjnych
• Rozwiązywania sporów i egzekwowania naszych umów
• Prowadzenia dokumentacji biznesowej wymaganej przez prawo

Zazwyczaj przechowujemy:
• Informacje o koncie: Czas trwania istnienia konta plus 7 lat
• Rejestry zamówień: 10 lat dla celów podatkowych i zgodności z prawem
• Preferencje marketingowe: Do momentu rezygnacji
• Analitykę strony: 26 miesięcy

Po upływie okresu przechowywania Twoje dane zostaną bezpiecznie usunięte lub zanonimizowane.`
        },
        {
          icon: Shield,
          title: '7. Pliki cookie i śledzenie',
          content: `Nasza strona wykorzystuje pliki cookie i podobne technologie w celu:

Niezbędne pliki cookie:
• Umożliwienia podstawowego działania strony internetowej
• Utrzymania Twojej sesji i koszyka zakupów
• Zapamiętania preferencji językowych

Analityczne pliki cookie:
• Zrozumienia, w jaki sposób odwiedzający korzystają z naszej strony
• Ulepszania doświadczeń użytkowników i wydajności
• Generowania anonimowych statystyk użytkowania

Możesz zarządzać plikami cookie w ustawieniach swojej przeglądarki. Pamiętaj, że wyłączenie niektórych plików cookie może wpłynąć na funkcjonowanie strony.

Nie wykorzystujemy plików cookie do reklam stron trzecich ani do śledzenia użytkowników w innych witrynach.`
        },
        {
          icon: Mail,
          title: '8. Kontakt i aktualizacje',
          content: `Jeśli masz pytania dotyczące niniejszej Polityki prywatności lub naszych praktyk w zakresie danych, skontaktuj się z nami:

E‑mail: info@penlabpeptide.com
Adres: España, Europa

Aktualizacje polityki:
Możemy okresowo aktualizować niniejszą Politykę prywatności, aby odzwierciedlać zmiany w naszych praktykach lub wymogach prawnych. O istotnych zmianach poinformujemy Cię poprzez:
• Zamieszczenie powiadomienia na naszej stronie internetowej
• Wysłanie wiadomości e‑mail do zarejestrowanych użytkowników
• Zaktualizowanie daty „Ostatnia aktualizacja”

Zachęcamy do regularnego przeglądania tej polityki, aby być na bieżąco z tym, jak chronimy Twoje dane.`
        }
      ],
      contact: 'Skontaktuj się z naszym zespołem ds. prywatności',
      contactText: 'W przypadku pytań dotyczących prywatności lub w celu skorzystania ze swoich praw do danych, skontaktuj się z nami pod adresem:',
      email: 'info@penlabpeptide.com'
    },
    nl: {
      badge: "Juridisch",
      title: 'Privacybeleid',
      lastUpdated: 'Laatst bijgewerkt',
      intro: 'Bij PenLab Peptide zetten wij ons in om uw privacy te beschermen en de veiligheid van uw persoonlijke gegevens te waarborgen. Dit privacybeleid legt uit hoe wij uw gegevens verzamelen, gebruiken, openbaar maken en beveiligen wanneer u onze website bezoekt of een aankoop doet.',
      sections: [
        {
          icon: Database,
          title: '1. Informatie die wij verzamelen',
          content: `Wij verzamelen informatie die u rechtstreeks aan ons verstrekt, waaronder:

Persoonlijke gegevens:
• Naam en contactgegevens (e-mail, telefoonnummer, adres)
• Professionele referenties en institutionele affiliatie
• Licentie-/registratienummers voor verificatiedoeleinden
• Betaal- en facturatiegegevens

Automatisch verzamelde informatie:
• IP-adres en browsertype
• Apparaatgegevens en besturingssysteem
• Bezochte pagina's en de tijd die op onze website is doorgebracht
• Cookies en vergelijkbare trackingtechnologieën

Wij verzamelen deze informatie om uw geschiktheid te verifiëren, bestellingen te verwerken en onze diensten te verbeteren.`
        },
        {
          icon: Eye,
          title: '2. Hoe wij uw informatie gebruiken',
          content: `Wij gebruiken de informatie die wij verzamelen voor de volgende doeleinden:

• Accountverificatie en bevestiging van geschiktheid
• Verwerken en uitvoeren van uw bestellingen
• Met u communiceren over uw bestellingen en account
• Verzenden van belangrijke updates over onze producten en diensten
• Verbeteren van onze website en klantervaring
• Voldoen aan wettelijke verplichtingen en regelgevende vereisten
• Voorkomen van fraude en waarborgen van veiligheid
• Analyseren van gebruikspatronen om onze diensten te verbeteren

Wij zullen uw persoonlijke informatie nooit aan derden verkopen voor marketingdoeleinden.`
        },
        {
          icon: Lock,
          title: '3. Gegevensbeveiliging',
          content: `Wij implementeren passende technische en organisatorische maatregelen om uw persoonlijke gegevens te beschermen, waaronder:

• SSL/TLS-versleuteling voor alle gegevensoverdrachten
• Beveiligde servers met beperkte toegang
• Regelmatige beveiligingsaudits en kwetsbaarheidsbeoordelingen
• Training van medewerkers over gegevensbeschermingspraktijken
• Veilige betalingsverwerking via vertrouwde aanbieders

Hoewel wij ons inspannen om uw gegevens te beschermen, is geen enkele methode van overdracht via het internet 100% veilig. Wij kunnen geen absolute veiligheid garanderen, maar zetten ons in om de hoogste normen voor gegevensbescherming te handhaven.`
        },
        {
          icon: Globe,
          title: '4. Delen van informatie',
          content: `Wij kunnen uw gegevens delen met:

Dienstverleners:
• Vervoerders (DHL, enz.) voor de bezorging van bestellingen
• Betaalverwerkers voor de verwerking van transacties
• IT-dienstverleners voor het onderhoud van de website

Wettelijke vereisten:
• Wanneer dit vereist is door wet- of regelgeving of juridische procedures
• Om onze rechten en eigendommen te beschermen
• Om fraude of illegale activiteiten te voorkomen
• Om te voldoen aan regelgevende verplichtingen

Zakelijke overdrachten:
• In verband met een fusie, overname of verkoop van activa

Wij verplichten alle derden om uw privacy te respecteren en uw gegevens te behandelen in overeenstemming met de toepasselijke wetgeving.`
        },
        {
          icon: UserCheck,
          title: '5. Uw rechten',
          content: `Op grond van de toepasselijke wetgeving inzake gegevensbescherming (waaronder de AVG) heeft u het recht op:

• Inzage: Een kopie van uw persoonsgegevens opvragen
• Rectificatie: Onjuiste of onvolledige gegevens laten corrigeren
• Wissen: Verwijdering van uw gegevens verzoeken ("recht om vergeten te worden")
• Beperking: Beperken hoe wij uw gegevens verwerken
• Overdraagbaarheid: Uw gegevens in een draagbaar formaat ontvangen
• Bezwaar: Bezwaar maken tegen bepaalde verwerkingsactiviteiten
• Intrekken van toestemming: Uw toestemming op elk moment intrekken

Om deze rechten uit te oefenen, kunt u contact met ons opnemen via info@penlabpeptide.com. Wij zullen binnen 30 dagen op uw verzoek reageren.`
        },
        {
          icon: Clock,
          title: '6. Bewaartermijn van gegevens',
          content: `Wij bewaren uw persoonlijke gegevens zolang als nodig is om:

• De doeleinden te vervullen waarvoor deze zijn verzameld
• Te voldoen aan wettelijke en regelgevende vereisten
• Geschillen op te lossen en onze overeenkomsten af te dwingen
• Bedrijfsadministratie te voeren zoals wettelijk vereist

In de regel bewaren wij:
• Accountgegevens: Duur van uw account plus 7 jaar
• Bestelgegevens: 10 jaar voor fiscale en juridische naleving
• Marketingvoorkeuren: Totdat u zich afmeldt
• Website-analysegegevens: 26 maanden

Na afloop van de bewaartermijn worden uw gegevens veilig verwijderd of geanonimiseerd.`
        },
        {
          icon: Shield,
          title: '7. Cookies & tracking',
          content: `Onze website gebruikt cookies en vergelijkbare technologieën om:

Essentiële cookies:
• De kernfunctionaliteit van de website mogelijk te maken
• Uw sessie en winkelwagen te behouden
• Uw taalvoorkeuren te onthouden

Analyse-cookies:
• Te begrijpen hoe bezoekers onze website gebruiken
• De gebruikerservaring en prestaties te verbeteren
• Anonieme gebruiksstatistieken te genereren

U kunt cookies beheren via de instellingen van uw browser. Let op dat het uitschakelen van bepaalde cookies de functionaliteit van de website kan beïnvloeden.

Wij gebruiken geen cookies voor reclame door derden of voor tracking op andere websites.`
        },
        {
          icon: Mail,
          title: '8. Contact & updates',
          content: `Als u vragen heeft over dit privacybeleid of onze gegevensverwerking, neem dan contact met ons op:

E-mail: info@penlabpeptide.com
Adres: España, Europa

Updates van het beleid:
Wij kunnen dit privacybeleid periodiek bijwerken om veranderingen in onze praktijken of wettelijke vereisten weer te geven. Wij zullen u op de hoogte brengen van belangrijke wijzigingen door:
• Een melding op onze website te plaatsen
• Een e-mail te sturen naar geregistreerde gebruikers
• De datum "Laatst bijgewerkt" bij te werken

Wij raden u aan dit beleid regelmatig te raadplegen om op de hoogte te blijven van hoe wij uw gegevens beschermen.`
        }
      ],
      contact: 'Neem contact op met ons privacyteam',
      contactText: 'Voor vragen over privacy of om uw gegevensrechten uit te oefenen, kunt u contact met ons opnemen via:',
      email: 'info@penlabpeptide.com'
    },
    pt: {
      badge: "Jurídico",
      title: 'Política de Privacidade',
      lastUpdated: 'Última atualização',
      intro: 'Na PenLab Peptide, estamos comprometidos em proteger sua privacidade e garantir a segurança de suas informações pessoais. Esta Política de Privacidade explica como coletamos, usamos, divulgamos e protegemos suas informações quando você visita nosso site ou faz uma compra.',
      sections: [
        {
          icon: Database,
          title: '1. Informações que Coletamos',
          content: `Coletamos informações que você fornece diretamente para nós, incluindo:

Informações Pessoais:
• Nome e dados de contato (e-mail, número de telefone, endereço)
• Credenciais profissionais e afiliação institucional
• Números de licença/registro para fins de verificação
• Informações de pagamento e faturamento

Informações Coletadas Automaticamente:
• Endereço IP e tipo de navegador
• Informações do dispositivo e sistema operacional
• Páginas visitadas e tempo gasto em nosso site
• Cookies e tecnologias de rastreamento semelhantes

Coletamos essas informações para verificar sua elegibilidade, processar pedidos e melhorar nossos serviços.`
        },
        {
          icon: Eye,
          title: '2. Como Usamos Suas Informações',
          content: `Usamos as informações que coletamos para as seguintes finalidades:

• Verificação de conta e confirmação de elegibilidade
• Processar e atender aos seus pedidos
• Comunicar-se com você sobre seus pedidos e conta
• Enviar atualizações importantes sobre nossos produtos e serviços
• Melhorar nosso site e a experiência do cliente
• Cumprir obrigações legais e requisitos regulatórios
• Prevenir fraudes e garantir segurança
• Analisar padrões de uso para aprimorar nossos serviços

Nunca venderemos suas informações pessoais a terceiros para fins de marketing.`
        },
        {
          icon: Lock,
          title: '3. Segurança de Dados',
          content: `Implementamos medidas técnicas e organizacionais adequadas para proteger suas informações pessoais, incluindo:

• Criptografia SSL/TLS para todas as transmissões de dados
• Servidores seguros com acesso restrito
• Auditorias de segurança e avaliações de vulnerabilidade regulares
• Treinamento de funcionários sobre práticas de proteção de dados
• Processamento de pagamentos seguro por meio de provedores confiáveis

Embora nos empenhemos para proteger suas informações, nenhum método de transmissão pela Internet é 100% seguro. Não podemos garantir segurança absoluta, mas estamos comprometidos em manter os mais altos padrões de proteção de dados.`
        },
        {
          icon: Globe,
          title: '4. Compartilhamento de Informações',
          content: `Podemos compartilhar suas informações com:

Provedores de Serviço:
• Transportadoras (DHL, etc.) para entrega de pedidos
• Processadores de pagamento para processamento de transações
• Provedores de serviços de TI para manutenção do site

Requisitos Legais:
• Quando exigido por lei ou processo legal
• Para proteger nossos direitos e propriedade
• Para prevenir fraudes ou atividades ilegais
• Para cumprir obrigações regulatórias

Transferências de Negócios:
• Em conexão com qualquer fusão, aquisição ou venda de ativos

Exigimos que todos os terceiros respeitem sua privacidade e tratem seus dados de acordo com as leis aplicáveis.`
        },
        {
          icon: UserCheck,
          title: '5. Seus Direitos',
          content: `De acordo com as leis de proteção de dados aplicáveis (incluindo o GDPR), você tem o direito de:

• Acesso: Solicitar uma cópia de seus dados pessoais
• Retificação: Corrigir dados imprecisos ou incompletos
• Eliminação: Solicitar a exclusão de seus dados ("direito ao esquecimento")
• Restrição: Limitar como processamos seus dados
• Portabilidade: Receber seus dados em um formato portátil
• Oposição: Opor-se a determinadas atividades de processamento
• Retirar Consentimento: Retirar o consentimento a qualquer momento

Para exercer esses direitos, entre em contato conosco em info@penlabpeptide.com. Responderemos à sua solicitação dentro de 30 dias.`
        },
        {
          icon: Clock,
          title: '6. Retenção de Dados',
          content: `Reteremos suas informações pessoais pelo tempo necessário para:

• Cumprir as finalidades para as quais foram coletadas
• Atender a requisitos legais e regulatórios
• Resolver disputas e fazer cumprir nossos acordos
• Manter registros comerciais conforme exigido por lei

Normalmente, retemos:
• Informações da conta: Duração da sua conta mais 7 anos
• Registros de pedidos: 10 anos para conformidade fiscal e legal
• Preferências de marketing: Até que você opte por sair
• Dados de análise do site: 26 meses

Após o período de retenção, seus dados serão excluídos com segurança ou anonimizados.`
        },
        {
          icon: Shield,
          title: '7. Cookies e Rastreamento',
          content: `Nosso site utiliza cookies e tecnologias semelhantes para:

Cookies Essenciais:
• Ativar funcionalidades principais do site
• Manter sua sessão e carrinho de compras
• Lembrar suas preferências de idioma

Cookies de Análise:
• Entender como os visitantes utilizam nosso site
• Melhorar a experiência do usuário e o desempenho
• Gerar estatísticas de uso anônimas

Você pode controlar os cookies por meio das configurações do seu navegador. Observe que desativar certos cookies pode afetar a funcionalidade do site.

Não utilizamos cookies para publicidade de terceiros ou rastreamento em outros sites.`
        },
        {
          icon: Mail,
          title: '8. Contato e Atualizações',
          content: `Se você tiver dúvidas sobre esta Política de Privacidade ou sobre nossas práticas de dados, entre em contato conosco:

E-mail: info@penlabpeptide.com
Endereço: España, Europa

Atualizações da Política:
Podemos atualizar periodicamente esta Política de Privacidade para refletir mudanças em nossas práticas ou exigências legais. Notificaremos você sobre alterações significativas por meio de:
• Publicação de um aviso em nosso site
• Envio de um e-mail para usuários registrados
• Atualização da data de "Última atualização"

Recomendamos que você revise esta política regularmente para se manter informado sobre como protegemos suas informações.`
        }
      ],
      contact: 'Entre em Contato com Nossa Equipe de Privacidade',
      contactText: 'Para quaisquer dúvidas relacionadas à privacidade ou para exercer seus direitos de dados, entre em contato conosco em:',
      email: 'info@penlabpeptide.com'
    },
  };

  const t = translations[currentLanguage] || translations.en;

  const iconColors = [
    'from-capsule-teal to-capsule-aqua',
    'from-green-500 to-emerald-500',
    'from-capsule-coral to-capsule-salmon',
    'from-purple-500 to-violet-500',
    'from-amber-500 to-orange-500',
    'from-blue-500 to-cyan-500',
    'from-capsule-teal to-capsule-aqua',
    'from-capsule-coral to-capsule-salmon'
  ];

  return (
    <div className="min-h-screen bg-capsule-bg">
      <Header />

      {/* Hero Banner - Capsule 8 Design */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-capsule-teal-dark">
        {/* DNA Decoration */}
        <div className="absolute right-0 top-0 bottom-0 w-96 opacity-20">
          <svg viewBox="0 0 200 400" fill="none" className="w-full h-full">
            <defs>
              <linearGradient id="privacyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#14B8A6" />
                <stop offset="100%" stopColor="#22D3EE" />
              </linearGradient>
            </defs>
            <path d="M50 0 Q120 50 50 100 Q-20 150 50 200 Q120 250 50 300 Q-20 350 50 400" 
                  stroke="url(#privacyGradient)" strokeWidth="3" fill="none"/>
            <path d="M150 0 Q80 50 150 100 Q220 150 150 200 Q80 250 150 300 Q220 350 150 400" 
                  stroke="url(#privacyGradient)" strokeWidth="3" fill="none"/>
          </svg>
        </div>
        
        {/* Floating spheres */}
        <div className="absolute top-20 right-32 w-20 h-20 rounded-full bg-gradient-to-br from-capsule-rose-gold/30 to-capsule-salmon/20 blur-xl"></div>
        <div className="absolute bottom-20 right-48 w-16 h-16 rounded-full bg-gradient-to-br from-capsule-teal/30 to-capsule-aqua/20 blur-xl"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="text-center max-w-3xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/20">
              <Shield className="w-4 h-4 text-capsule-aqua" />
              <span className="text-sm font-medium text-capsule-aqua">{t.badge}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              {t.title}
            </h1>
            <p className="text-lg text-gray-300">
              {t.lastUpdated}: December 2025
            </p>
          </div>
        </div>
        
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 37 768 43 864 45C960 47 1056 45 1152 41.7C1248 38 1344 33 1392 30.8L1440 29V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#E7EAED"/>
          </svg>
        </div>
      </div>
      
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Introduction */}
        <div className="glass-card rounded-3xl p-8 mb-8">
          <p className="text-gray-700 leading-relaxed text-lg">{t.intro}</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {t.sections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <div key={index} className="glass-card rounded-3xl p-8 hover:shadow-glass-hover transition-all duration-300">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 bg-gradient-to-br ${iconColors[index]} rounded-xl flex items-center justify-center shadow-lg`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h2>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact Section */}
        <div className="bg-gradient-to-r from-capsule-teal to-capsule-aqua rounded-3xl shadow-teal-glow p-8 mt-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-4">{t.contact}</h2>
          <p className="mb-6 opacity-90">{t.contactText}</p>
          <a 
            href={`mailto:${t.email}`}
            className="inline-flex items-center gap-2 bg-white text-capsule-teal px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors shadow-lg"
          >
            <Mail className="w-5 h-5" />
            {t.email}
          </a>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
